import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, SlidersHorizontal } from "lucide-react";

type Settings = {
  failure_threshold: number;
  critical_threshold: number;
  window_minutes: number;
  notifications_enabled: boolean;
  updated_at: string | null;
};

const schema = z.object({
  failure_threshold: z.coerce.number().int().min(1).max(50),
  critical_threshold: z.coerce.number().int().min(1).max(100),
  window_minutes: z.coerce.number().int().min(5).max(20160),
  notifications_enabled: z.boolean(),
}).refine((v) => v.critical_threshold >= v.failure_threshold, {
  message: "Critical threshold must be ≥ failure threshold",
  path: ["critical_threshold"],
});

export function CronAlertSettings() {
  const [s, setS] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("cron_alert_settings")
      .select("failure_threshold, critical_threshold, window_minutes, notifications_enabled, updated_at")
      .eq("id", true)
      .maybeSingle();
    setLoading(false);
    if (error) return toast.error(error.message);
    setS((data as Settings) ?? null);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!s) return;
    const parsed = schema.safeParse(s);
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setSaving(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("cron_alert_settings")
      .update({ ...parsed.data, updated_by: auth.user?.id ?? null })
      .eq("id", true);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Notification settings saved");
    load();
  };

  if (loading || !s) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="size-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-5">
      <div>
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <SlidersHorizontal className="size-4" /> Failure notification thresholds
        </h3>
        <p className="text-sm text-muted-foreground">
          Control how many failed retries (within a rolling window) trigger an alert email.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={s.notifications_enabled}
          onCheckedChange={(v) => setS({ ...s, notifications_enabled: v })}
        />
        <span className="text-sm">Send email notifications</span>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Field
          label="Failure threshold"
          hint="Failures inside the window before alerting"
          value={s.failure_threshold}
          min={1} max={50}
          onChange={(n) => setS({ ...s, failure_threshold: n })}
        />
        <Field
          label="Critical threshold"
          hint="Failures inside the window that escalate to critical"
          value={s.critical_threshold}
          min={1} max={100}
          onChange={(n) => setS({ ...s, critical_threshold: n })}
        />
        <Field
          label="Time window (minutes)"
          hint="Rolling window for counting failures"
          value={s.window_minutes}
          min={5} max={20160}
          onChange={(n) => setS({ ...s, window_minutes: n })}
        />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {s.updated_at ? `Last updated ${new Date(s.updated_at).toLocaleString()}` : ""}
        </span>
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}

function Field({
  label, hint, value, min, max, onChange,
}: {
  label: string; hint: string; value: number; min: number; max: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : ""}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
